/**
 * 百科数据模型 —— Swift Models.swift 的 TypeScript 镜像。
 * 字段、枚举与 Swift 端一一对应，行为以 Swift 解码/校验为准。
 */

export type EntryKind =
  | 'character'
  | 'achievement'
  | 'equipment'
  | 'furniture'
  | 'item'
  | 'crop'
  | 'fish'
  | 'monster'
  | 'recipe'
  | 'quest'
  | 'location'
  | 'mechanic';

export const ENTRY_KINDS: readonly EntryKind[] = [
  'character',
  'achievement',
  'equipment',
  'furniture',
  'item',
  'crop',
  'fish',
  'monster',
  'recipe',
  'quest',
  'location',
  'mechanic',
] as const;

export const entryKindTitle: Record<EntryKind, string> = {
  character: '人物',
  achievement: '成就',
  equipment: '装备',
  furniture: '家具',
  item: '物品',
  crop: '作物',
  fish: '鱼类',
  monster: '怪物',
  recipe: '配方',
  quest: '任务',
  location: '地点',
  mechanic: '机制',
};

/** SF Symbol 名，Web 端在 lib/icons.ts 映射为 Lucide 图标。 */
export const entryKindSymbolName: Record<EntryKind, string> = {
  character: 'person.2.fill',
  achievement: 'trophy.fill',
  equipment: 'shield.fill',
  furniture: 'chair.lounge.fill',
  item: 'shippingbox.fill',
  crop: 'leaf.fill',
  fish: 'fish.fill',
  monster: 'ant.fill',
  recipe: 'fork.knife',
  quest: 'checklist',
  location: 'map.fill',
  mechanic: 'gearshape.2.fill',
};

export type VerificationStatus = 'draft' | 'reviewed' | 'verified';

export type ResidentGroup = 'marriageCandidate' | 'townResident' | 'specialResident';

export type Season = 'spring' | 'summer' | 'fall' | 'winter';

export const SEASONS: readonly Season[] = ['spring', 'summer', 'fall', 'winter'] as const;

export const seasonTitle: Record<Season, string> = {
  spring: '春季',
  summer: '夏季',
  fall: '秋季',
  winter: '冬季',
};

export type GiftTaste = 'love' | 'like' | 'neutral' | 'dislike' | 'hate';

export const giftTasteTitle: Record<GiftTaste, string> = {
  love: '最爱',
  like: '喜欢',
  neutral: '普通',
  dislike: '不喜欢',
  hate: '讨厌',
};

export interface GiftTasteGroup {
  taste: GiftTaste;
  items: string[];
}

export interface ResidentScheduleProfile {
  sourcePages: number[];
  lines: string[];
}

export interface ResidentProfile {
  group: ResidentGroup;
  birthdaySeason: Season;
  birthdayDay: number;
  residence: string;
  marriageable: boolean;
  giftTastes: GiftTasteGroup[];
  universalGiftTastes?: GiftTasteGroup[];
  schedule?: ResidentScheduleProfile;
}

export type CropHarvestMethod = 'grab' | 'scythe';

export interface CropProfile {
  seedID: string;
  seedName: string;
  seedPrice: number;
  harvestItemID: string;
  seasons: Season[];
  daysInPhase: number[];
  maturityDays: number;
  regrowDays: number | null;
  needsWatering: boolean;
  isTrellis: boolean;
  isPaddyCrop: boolean;
  harvestMethod: CropHarvestMethod;
  harvestMinStack: number;
  harvestMaxStack: number;
  harvestMaxIncreasePerFarmingLevel: number;
  extraHarvestChance: number;
  sellPrice: number;
}

export type EquipmentType = 'weapon' | 'ring' | 'boots' | 'hat';

export const EQUIPMENT_TYPES: readonly EquipmentType[] = [
  'weapon',
  'ring',
  'boots',
  'hat',
] as const;

export const equipmentTypeTitle: Record<EquipmentType, string> = {
  weapon: '武器',
  ring: '戒指',
  boots: '鞋靴',
  hat: '帽子',
};

export type WeaponType = 'sword' | 'dagger' | 'club' | 'slingshot' | 'other';

export interface EquipmentProfile {
  type: EquipmentType;
  weaponType?: WeaponType;
  minDamage?: number;
  maxDamage?: number;
  knockback?: number;
  speed?: number;
  precision?: number;
  defense?: number;
  immunity?: number;
  areaOfEffect?: number;
  critChance?: number;
  critMultiplier?: number;
  canBeLostOnDeath?: boolean;
  price?: number;
}

export type RecipeType = 'cooking' | 'crafting';
export type RecipeOutputKind = 'object' | 'bigCraftable';
export type RecipeUnlockKind = 'default' | 'skill' | 'friendship' | 'television' | 'special';
export type RecipeIngredientKind = 'object' | 'bigCraftable';

export interface RecipeIngredient {
  itemID: string;
  name: string;
  quantity: number;
  kind: RecipeIngredientKind;
}

export interface RecipeBuff {
  label: string;
  value: string;
}

export interface RecipeProfile {
  type: RecipeType;
  ingredients: RecipeIngredient[];
  outputItemID: string;
  outputQuantity: number;
  outputKind: RecipeOutputKind;
  unlockKind: RecipeUnlockKind;
  unlockDescription: string;
  price: number;
  energy?: number;
  health?: number;
  isDrink?: boolean;
  buffs?: RecipeBuff[];
  isBigCraftable?: boolean;
  placement?: string;
}

export interface MonsterDrop {
  itemID: string;
  chance: number;
  name: string;
}

export interface MonsterProfile {
  internalName: string;
  health: number;
  damage: number;
  defense: number;
  missChance: number;
  isFlying: boolean;
  experience: number;
  drops: MonsterDrop[];
  spriteName: string;
}

export type FishWeather = 'sunny' | 'rainy' | 'any';

export interface FishTimeWindow {
  start: number;
  end: number;
}

export interface FishProfile {
  objectID: string;
  difficulty: number;
  behavior: string;
  minimumSize: number;
  maximumSize: number;
  timeWindows: FishTimeWindow[];
  seasons: Season[];
  weather: FishWeather;
  locations: string[];
  specialConditions: string[];
}

export interface EntryFact {
  label: string;
  value: string;
  /** 缺省为 false（对应 Swift 自定义解码 emphasis 的 decodeIfPresent）。 */
  emphasis?: boolean;
}

export interface EntrySection {
  title: string;
  body: string;
}

export type EntryReferenceRole =
  | 'ingredient'
  | 'product'
  | 'source'
  | 'location'
  | 'tool'
  | 'machine'
  | 'gift'
  | 'reward'
  | 'requirement';

export interface EntryReference {
  entryID: string;
  label?: string;
  quantity?: number;
  note?: string;
  role?: EntryReferenceRole;
}

export interface ReferenceListBlock {
  type: 'reference-list';
  title: string;
  references: EntryReference[];
}

export interface ConditionListBlock {
  type: 'condition-list';
  title: string;
  conditions: string[];
}

export interface DataTableBlock {
  type: 'data-table';
  title: string;
  columns: string[];
  rows: string[][];
}

export interface TimelineBlock {
  type: 'timeline';
  title: string;
  items: { label: string; body: string; entryIDs?: string[] }[];
}

export interface CalloutBlock {
  type: 'callout';
  title: string;
  body: string;
  tone: 'tip' | 'warning' | 'priority';
}

export type EntryContentBlock =
  | ReferenceListBlock
  | ConditionListBlock
  | DataTableBlock
  | TimelineBlock
  | CalloutBlock;

export interface EncyclopediaEntry {
  id: string;
  kind: EntryKind;
  topicID: string;
  nameZh: string;
  nameEn: string;
  aliases: string[];
  summary: string;
  facts: EntryFact[];
  sections: EntrySection[];
  contentBlocks?: EntryContentBlock[];
  relatedEntryIDs: string[];
  gameVersion: string;
  imageKey: string;
  sources: string[];
  verification: VerificationStatus;
  residentProfile?: ResidentProfile;
  cropProfile?: CropProfile;
  equipmentProfile?: EquipmentProfile;
  recipeProfile?: RecipeProfile;
  monsterProfile?: MonsterProfile;
  fishProfile?: FishProfile;
}

/** 解码后的原始 JSON 结构（emphasis 可能缺失，decodeEntry 归一化）。 */
interface RawEntryFact extends Omit<EntryFact, 'emphasis'> {
  emphasis?: boolean;
}

interface RawEntry extends Omit<EncyclopediaEntry, 'facts' | 'kind'> {
  /** 旧 schema 用 category 字段；Swift 解码 kind 缺失时回退到 category。 */
  kind?: EntryKind;
  category?: EntryKind;
  facts: RawEntryFact[];
}

export interface ContentPackage {
  schemaVersion: number;
  gameVersion: string;
  generatedAt: string;
  entries: EncyclopediaEntry[];
}

interface RawContentPackage {
  schemaVersion: number;
  gameVersion: string;
  generatedAt: string;
  entries: RawEntry[];
}

/**
 * 解码单条条目：镜像 Swift EncyclopediaEntry.init(from:)。
 * - kind 缺失时回退到 category（旧 schema 兼容）。
 * - facts.emphasis 缺失时默认 false。
 */
export function decodeEntry(raw: RawEntry): EncyclopediaEntry {
  const { kind, category, facts, ...rest } = raw;
  return {
    ...rest,
    kind: kind ?? category ?? 'mechanic',
    facts: facts.map((f) => ({ ...f, emphasis: f.emphasis ?? false })),
  };
}

/** 解码整包内容：镜像 Swift JSONDecoder().decode(ContentPackage.self)。 */
export function decodeContentPackage(raw: RawContentPackage): ContentPackage {
  return {
    schemaVersion: raw.schemaVersion,
    gameVersion: raw.gameVersion,
    generatedAt: raw.generatedAt,
    entries: raw.entries.map(decodeEntry),
  };
}
